/**
 * MetricCard Component
 * Feature: 003-admin-dashboard-metrics
 * 
 * Displays a metric card showing active and archived counts
 * Includes loading skeleton, error states, and retry functionality
 */

import { useTranslation } from 'react-i18next';
import type { MetricCardProps } from '../../types/adminMetrics';

/**
 * Format a number with thousand separators based on locale
 */
function formatCount(count: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(count);
}

export function MetricCard({
  title,
  metric,
  loading,
  error,
  onRetry,
}: MetricCardProps) {
  const { t, i18n } = useTranslation('auth');

  // Loading skeleton
  if (loading) {
    return (
      <div className="rounded-lg bg-white/10 backdrop-blur-md border border-white/20 p-6 shadow-lg">
        <div className="animate-pulse">
          <div className="h-6 bg-white/20 rounded w-24 mb-4"></div>
          <div className="space-y-3">
            <div className="h-8 bg-white/20 rounded w-32"></div>
            <div className="h-8 bg-white/20 rounded w-32"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="rounded-lg bg-white/10 backdrop-blur-md border border-red-400/30 p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
        <div className="text-red-300 text-sm mb-3">
          {t('dashboard.metrics.error')}
        </div>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 rounded-lg text-red-200 text-sm transition-colors"
        >
          {t('dashboard.metrics.retry')}
        </button>
      </div>
    );
  }

  // Success state with data
  if (!metric) {
    return null;
  }

  const activeCount = formatCount(metric.active, i18n.language);
  const archivedCount = formatCount(metric.archived, i18n.language);

  return (
    <div className="rounded-lg bg-white border border-neutral-200 p-6 shadow-lg hover:shadow-xl transition-shadow">
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">{title}</h3>
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-neutral-700">
            {t('dashboard.metrics.active')}:
          </span>
          <span
            className="text-2xl font-bold text-emerald-600"
            aria-label={`${metric.active} ${t('dashboard.metrics.active')} ${title}`}
          >
            {activeCount}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-neutral-700">
            {t('dashboard.metrics.archived')}:
          </span>
          <span
            className="text-2xl font-bold text-neutral-500"
            aria-label={`${metric.archived} ${t('dashboard.metrics.archived')} ${title}`}
          >
            {archivedCount}
          </span>
        </div>
      </div>
    </div>
  );
}
